import type { ReactElement } from 'react';
import { createRootRoute, createRoute, createRouter, Navigate, redirect } from '@tanstack/react-router';
import { minPlanFor } from '@dale/plans';

import { RootLayout } from './App';
import { createAdminRoutes } from './admin/routes';
import { EnlaceReservaPage } from './routes/EnlaceReserva';
import { RequireAuth, RequireMustChangePassword } from './auth/RequireAuth';
import { RequireMode } from './auth/RequireMode';
import { RequirePlan } from './auth/RequirePlan';
import { firstPermittedLanding } from './auth/landing';
import { isCoach, isOwner } from './auth/roles';
import { usePermissions } from './auth/usePermissions';
import { modeForCategory } from './lib/mode';
import { parseDaysParam } from './lib/offer-days';
import { useSession } from './auth/useSession';
import { isPlanTier, meetsPlan, type PlanTier } from './lib/plan';
import { useBusiness } from './lib/useBusiness';
import { DashboardPage } from './routes/Dashboard';
import { EmptyState } from './components/EmptyState';
import { LoadingState } from './components/LoadingState';
import { AgendaPage } from './routes/Agenda';
import { AjustesPage } from './routes/Ajustes';
import { BandejaPage } from './routes/Bandeja';
import { BookingDetailPage } from './routes/BookingDetail';
import { CambiarContrasenaPage } from './routes/CambiarContrasena';
import { ClientesPage } from './routes/Clientes';
import { ReportesPage } from './routes/Reportes';
import { InvitacionPage } from './routes/Invitacion';
import { RegistroPage } from './routes/Registro';
import { CobrarPage } from './routes/Cobrar';
import { EntrarPage } from './routes/Entrar';
import { EquipoPage } from './routes/Equipo';
import { FacturaPrintPage } from './routes/FacturaPrint';
import { FacturaPublicaPage } from './routes/FacturaPublica';
import { FacturasPage } from './routes/Facturas';
import { FinanzasPage } from './routes/Finanzas';
import { HistorialPage } from './routes/Historial';
import { PagosPage } from './routes/Pagos';
import { InboxPage } from './routes/Inbox';
import { CalendarioPage } from './routes/Calendario';
import { InteligenciaPage } from './routes/Inteligencia';
import { InteligenciaInformePage } from './routes/InteligenciaInforme';
import { AgentesPage } from './routes/Agentes';
import { ClasesPage } from './routes/Clases';
import { ComandasPage } from './routes/Comandas';
import { PlanesPage } from './routes/Planes';
import { MesasPage } from './routes/Mesas';
import { NegocioPage } from './routes/Negocio';
import { ConfiguracionPage } from './routes/Configuracion';
import { HerramientasPage } from './routes/Herramientas';
import { MembresiaPage } from './routes/Membresia';
import { OfertasPage } from './routes/Ofertas';
import { OnboardingPage } from './routes/Onboarding';
import { PagoPage } from './routes/Pago';
import { ServiciosPage } from './routes/Servicios';

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: () => <EmptyState title="No encontramos esa página." />,
});

const entrarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entrar',
  component: EntrarPage,
});

const registroRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/registro',
  component: RegistroPage,
});

// The independent signup: one person working alone, always on Independientes.
const registroIndependienteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/registro/independiente',
  component: () => <RegistroPage mode="independiente" />,
});

// Martín's invitation link: a password opens the signup with three months free on any plan.
const registroInvitacionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/registro/invitacion',
  component: InvitacionPage,
});

const cambiarContrasenaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cambiar-contrasena',
  component: () => (
    <RequireMustChangePassword>
      <CambiarContrasenaPage />
    </RequireMustChangePassword>
  ),
});

type InboxNotice = 'solo-dueno' | 'sin-permiso';
type InboxSearch = { notice?: InboxNotice };

function parseInboxNotice(value: unknown): InboxNotice | undefined {
  return value === 'solo-dueno' || value === 'sin-permiso' ? value : undefined;
}

const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): InboxSearch => ({
    notice: parseInboxNotice(search.notice),
  }),
  component: HomeRoute,
});

/**
 * Owners open on the day's summary; the team opens straight on the agenda.
 * A Reservas-tier business has no cobros to summarise, so its owner opens on
 * the agenda too — that tier IS the reservations book.
 */
function HomeRoute() {
  const search = inboxRoute.useSearch();
  const { session } = useSession();
  const business = useBusiness();
  const { can, ready } = usePermissions();
  const showDashboard = Boolean(session && isOwner(session.role)) && meetsPlan(business?.plan, minPlanFor('inicio'));
  // A studio coach's day is their classes, so that is where they land.
  if (isCoach(session) && business && modeForCategory(business.category) === 'classes') {
    return <Navigate to="/clases" replace />;
  }
  // The team's Inicio is the agenda; a profile without it opens on its first door instead.
  const teammate = Boolean(session) && !isOwner(session?.role) && !isCoach(session);
  if (teammate && !ready) return <LoadingState label="Cargando tus permisos…" />;
  if (teammate && !can('agenda')) {
    const landing = firstPermittedLanding(can);
    if (landing) return <Navigate to={landing} replace />;
    return (
      <RequireAuth>
        <main>
          <EmptyState
            title="Todavía no tienes secciones abiertas."
            description="Pídele a la persona dueña que te dé acceso en Equipo."
          />
        </main>
      </RequireAuth>
    );
  }
  return (
    <RequireAuth>
      {showDashboard ? (
        <DashboardPage />
      ) : (
        <InboxPage {...(search.notice ? { notice: search.notice } : {})} />
      )}
    </RequireAuth>
  );
}

const calendarioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/calendario',
  component: () => (
    <RequireAuth permission="agenda" allowCoach>
      <CalendarioPage />
    </RequireAuth>
  ),
});

const reservasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reservas',
  component: () => (
    <RequireAuth permission="agenda" allowCoach>
      <InboxPage />
    </RequireAuth>
  ),
});

// A customer writes on WhatsApp; the team sends back a link with the service and time chosen.
const enlaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enlace',
  component: () => (
    <RequireAuth permission="agenda" allowCoach>
      <EnlaceReservaPage />
    </RequireAuth>
  ),
});

const bookingDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reservas/$bookingId',
  component: () => (
    <RequireAuth permission="agenda" allowCoach>
      <BookingDetailPage />
    </RequireAuth>
  ),
});

const agendaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agenda',
  component: () => (
    <RequireAuth permission="horario">
      <AgendaPage />
    </RequireAuth>
  ),
});

const clasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clases',
  component: () => (
    // Classes start at Base: studios are not independientes.
    <RequireAuth>
      <RequirePlan capability="clases">
        <ClasesPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

/** Counter work, so the whole team gets it: an owner-only comanda is useless. */
const comandasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/comandas',
  component: () => (
    <RequireAuth permission="cobrar">
      <RequirePlan capability="comandas">
        <RequireMode mode="restaurant">
          <ComandasPage />
        </RequireMode>
      </RequirePlan>
    </RequireAuth>
  ),
});

const planesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planes',
  component: () => (
    <RequireAuth permission="servicios">
      <RequirePlan capability="planes">
        <PlanesPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const mesasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mesas',
  component: () => (
    <RequireAuth permission="servicios">
      <RequirePlan capability="mesas">
        <MesasPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const equipoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/equipo',
  component: () => (
    <RequireAuth permission="horario">
      <RequirePlan capability="equipo">
        <EquipoPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const serviciosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/servicios',
  component: () => (
    <RequireAuth permission="servicios">
      <ServiciosPage />
    </RequireAuth>
  ),
});

type OfertasSearch = { dias?: string };

function OfertasRouteComponent(): ReactElement {
  const { dias } = ofertasRoute.useSearch();
  return (
    <RequireAuth permission="servicios">
      <RequirePlan capability="ofertas">
        <OfertasPage initialDays={parseDaysParam(dias)} />
      </RequirePlan>
    </RequireAuth>
  );
}

const ofertasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ofertas',
  // Inteligencia's dead-hours link lands here with its weak day picked (`?dias=2`).
  validateSearch: (search: Record<string, unknown>): OfertasSearch => {
    const days = parseDaysParam(search.dias);
    return days.length > 0 ? { dias: days.join(',') } : {};
  },
  component: OfertasRouteComponent,
});

const negocioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/negocio',
  component: () => (
    <RequireAuth permission="negocio">
      <NegocioPage />
    </RequireAuth>
  ),
});

type MembresiaSearch = { plan?: PlanTier };

function MembresiaRouteComponent(): ReactElement {
  const { plan } = membresiaRoute.useSearch();
  return (
    <RequireAuth ownerOnly webOnly>
      <MembresiaPage initialPlan={plan} />
    </RequireAuth>
  );
}

const membresiaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/membresia',
  // A lock's "Cambiar a Pro" lands here with that plan already picked (`?plan=pro`).
  validateSearch: (search: Record<string, unknown>): MembresiaSearch =>
    typeof search.plan === 'string' && isPlanTier(search.plan) ? { plan: search.plan } : {},
  component: MembresiaRouteComponent,
});

// The Helios top bar's gear: the index of the administration pages.
const ajustesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ajustes',
  component: () => (
    <RequireAuth>
      <AjustesPage />
    </RequireAuth>
  ),
});

// Ajustes, Herramientas: the owner picks which tools show for the whole team.
const herramientasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/herramientas',
  component: () => (
    <RequireAuth ownerOnly>
      <HerramientasPage />
    </RequireAuth>
  ),
});

// Opened from the profile at the foot of the sidebar; the whole team has one.
const configuracionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/configuracion',
  component: () => (
    <RequireAuth>
      <ConfiguracionPage />
    </RequireAuth>
  ),
});

type PagoSearch = { pago?: string; cancelado?: '1'; desde?: 'registro'; stripe?: 'listo' | 'cancelado' };

/**
 * El pago del plan. PagueloFácil and PayPal send the owner back here with
 * ?pago=<id> (plus their own parameters, ignored); the page asks the server
 * what happened. Owner-only, and not gated on approval: a new business pays
 * before Dale approves it.
 */
const pagoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pago',
  validateSearch: (search: Record<string, unknown>): PagoSearch => ({
    ...(typeof search.pago === 'string' && search.pago ? { pago: search.pago } : {}),
    ...(search.cancelado === '1' || search.cancelado === 1 ? { cancelado: '1' as const } : {}),
    ...(search.desde === 'registro' ? { desde: 'registro' as const } : {}),
    ...(search.stripe === 'listo' || search.stripe === 'cancelado' ? { stripe: search.stripe } : {}),
  }),
  component: PagoRoute,
});

/** Also the card step: Stripe sends the owner back with ?stripe=listo or ?stripe=cancelado. */
function PagoRoute() {
  const { pago, cancelado, desde, stripe } = pagoRoute.useSearch();
  return (
    <RequireAuth ownerOnly allowCardLocked webOnly>
      <PagoPage
        {...(pago ? { paymentId: pago } : {})}
        cancelled={cancelado === '1' || stripe === 'cancelado'}
        fromSignup={desde === 'registro'}
        {...(stripe === 'listo' ? { stripeReturned: true } : {})}
      />
    </RequireAuth>
  );
}

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/primer-dia',
  component: () => (
    <RequireAuth ownerOnly>
      <OnboardingPage />
    </RequireAuth>
  ),
});

type CobrarSearch = { reserva?: string };

const cobrarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cobrar',
  validateSearch: (search: Record<string, unknown>): CobrarSearch =>
    typeof search.reserva === 'string' && search.reserva ? { reserva: search.reserva } : {},
  component: CobrarRoute,
});

function CobrarRoute() {
  const { reserva } = cobrarRoute.useSearch();
  return (
    <RequireAuth permission="cobrar">
      <RequirePlan capability="cobrar">
        <CobrarPage {...(reserva ? { initialBookingId: reserva } : {})} />
      </RequirePlan>
    </RequireAuth>
  );
}

type ClientesSearch = { buscar?: string; ids?: string };
/** Inteligencia links at most this many customers; the ids are opaque, so only their shape is checked. */
const MAX_LINKED_CUSTOMER_IDS = 100;
const CUSTOMER_ID_SHAPE = /^[A-Za-z0-9_-]{1,64}$/;

function readLinkedIds(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const ids = value.split(',').map((id) => id.trim()).filter((id) => CUSTOMER_ID_SHAPE.test(id)).slice(0, MAX_LINKED_CUSTOMER_IDS);
  return ids.length > 0 ? ids.join(',') : undefined;
}

function ClientesRouteComponent(): ReactElement {
  const { buscar, ids } = clientesRoute.useSearch();
  return (
    <RequireAuth permission="clientes">
      <RequirePlan capability="clientes">
        <ClientesPage initialQuery={buscar} initialIds={ids?.split(',')} />
      </RequirePlan>
    </RequireAuth>
  );
}

const clientesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clientes',
  // The workspace bar's "Buscar clientes" lands here with the words already in the box.
  validateSearch: (search: Record<string, unknown>): ClientesSearch => {
    const ids = readLinkedIds(search.ids);
    return {
      ...(typeof search.buscar === 'string' && search.buscar.trim() ? { buscar: search.buscar.trim().slice(0, 80) } : {}),
      ...(ids ? { ids } : {}),
    };
  },
  component: ClientesRouteComponent,
});

const historialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/historial',
  component: () => (
    <RequireAuth permission="pagos">
      <RequirePlan capability="historial">
        <HistorialPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

/** Every tier, every member: the free Reservas tier IS a paseador with a cash box. */
const pagosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pagos',
  component: () => (
    <RequireAuth permission="pagos">
      <PagosPage />
    </RequireAuth>
  ),
});

const finanzasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/finanzas',
  component: () => (
    <RequireAuth permission="finanzas">
      <RequirePlan capability="finanzas">
        <FinanzasPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const reportesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/finanzas/reportes',
  component: () => (
    <RequireAuth permission="finanzas">
      <RequirePlan capability="finanzas">
        <ReportesPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const facturasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/facturas',
  component: () => (
    <RequireAuth permission="facturas">
      <RequirePlan capability="facturas">
        <FacturasPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const agentesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agentes',
  component: () => (
    <RequireAuth ownerOnly>
      <RequirePlan capability="agentes">
        <AgentesPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

// Dale Agents: Luna's WhatsApp conversations, which the team can take over.
const bandejaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bandeja',
  component: () => (
    <RequireAuth ownerOnly>
      <RequirePlan capability="recepcionista">
        <BandejaPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const inteligenciaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inteligencia',
  component: () => (
    <RequireAuth permission="inteligencia">
      <RequirePlan capability="inteligencia">
        <InteligenciaPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const inteligenciaInformeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inteligencia/$reportId',
  component: () => (
    <RequireAuth ownerOnly>
      <RequirePlan capability="inteligencia">
        <InteligenciaInformePage />
      </RequirePlan>
    </RequireAuth>
  ),
});

const facturaPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/facturas/$invoiceId/imprimir',
  component: () => (
    <RequireAuth permission="facturas">
      <RequirePlan capability="facturas">
        <FacturaPrintPage />
      </RequirePlan>
    </RequireAuth>
  ),
});

/**
 * La factura pública. NO `RequireAuth` and no `RequirePlan` on purpose: this is
 * the link the customer was emailed, and it has to open for somebody who has
 * never heard of the panel. Its authorization is the 32-character token in the
 * path, checked by the API and by nothing here.
 */
const facturaPublicaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/factura/$shareToken',
  component: FacturaPublicaPage,
});

/** Renamed to Finanzas (docs/CONCEPTS.md): the old URL keeps working, it just bounces onward. */
const facturacionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/facturacion',
  beforeLoad: () => {
    throw redirect({ to: '/finanzas' });
  },
});

const adminRoutes = createAdminRoutes(rootRoute);

const routeTree = rootRoute.addChildren([
  entrarRoute,
  registroRoute,
  registroInvitacionRoute,
  registroIndependienteRoute,
  cambiarContrasenaRoute,
  inboxRoute,
  reservasRoute,
  enlaceRoute,
  calendarioRoute,
  bookingDetailRoute,
  agendaRoute,
  clasesRoute,
  comandasRoute,
  planesRoute,
  mesasRoute,
  equipoRoute,
  serviciosRoute,
  ofertasRoute,
  negocioRoute,
  membresiaRoute,
  herramientasRoute,
  configuracionRoute,
  ajustesRoute,
  bandejaRoute,
  pagoRoute,
  onboardingRoute,
  cobrarRoute,
  clientesRoute,
  historialRoute,
  pagosRoute,
  finanzasRoute,
  reportesRoute,
  facturasRoute,
  facturaPrintRoute,
  facturaPublicaRoute,
  facturacionRoute,
  inteligenciaRoute,
  inteligenciaInformeRoute,
  agentesRoute,
  ...adminRoutes,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
